import { makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import gatheringsAPI, {
  GatheringNote as GatheringNoteType,
} from '../../api/gatherings';
import {
  Note,
  NewNote,
  PageLoader,
  SnackMessage,
} from '../../components/common';
import { GatheringContext } from '../../contexts/gathering-context';
import { StringDate } from '../../utils/date/StringDate';

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
  loader: {
    width: '100%',
    minHeight: 400,
    maxHeight: '100%',
    display: 'flex',
    alignItems: 'center',
  },
});

function GatheringNotesPage() {
  const classes = useStyles();
  const { gatheringId } = useParams<{ gatheringId: string }>();
  const { gathering } = useContext(GatheringContext);

  const [updatingNotesIds, setUpdaingNotesIds] = useState<{
    [x: string]: boolean;
  }>({});
  const [deletingNotesIds, setDeletingNotesIds] = useState<{
    [x: string]: boolean;
  }>({});
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notes, setNotes] = useState<GatheringNoteType[]>([]);
  const [activeNoteForm, setActiveNoteForm] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleCreateNote = useCallback(
    async (
      note: {
        contents: string;
        attachmentRefs: string;
        creationDate: StringDate;
      },
      callback,
    ) => {
      try {
        setIsCreating(true);
        if (gathering) {
          const createdNotes = (await gatheringsAPI.createNote({
            gatheringId: gathering.id,
            contents: note.contents,
            attachmentRefs: note.attachmentRefs,
            creationDate: note.creationDate,
          })) as GatheringNoteType;
          setNotes((prev) => [createdNotes, ...prev]);
        }
        setIsCreating(false);
        callback();
        enqueueSnackbar('The note was successfully added.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while creating the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setIsCreating(false);
      }
    },
    [gathering, enqueueSnackbar],
  );

  const handleNoteUpdate = useCallback(
    async (note: GatheringNoteType, callback) => {
      try {
        setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: true }));
        if (gathering) {
          await gatheringsAPI.updateNote(note);
          setNotes((prev) =>
            prev.map((gatheringNotes) =>
              gatheringNotes.id === note.id ? note : gatheringNotes,
            ),
          );
        }
        setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: false }));
        callback();
        enqueueSnackbar('The note was successfully updated.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while updating the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: false }));
      }
    },
    [gathering, enqueueSnackbar],
  );

  const handleNoteRemove = useCallback(
    async (note: GatheringNoteType, callback: () => any) => {
      try {
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: true }));
        if (gathering) {
          await gatheringsAPI.removeNote(gathering.id, note.id);
          setNotes((prev) =>
            prev.filter((gatheringNote) => gatheringNote.id !== note.id),
          );
        }
        callback();
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: false }));
        enqueueSnackbar('The note was successfully removed.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while removing the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: false }));
      }
    },
    [gathering, enqueueSnackbar],
  );

  const handleUploadFile = useCallback(
    async (file: File) => {
      try {
        const loadedFileURL = await gatheringsAPI.attachFileToNote(
          gatheringId,
          file,
        );
        return loadedFileURL;
      } catch (e: any) {
        return '';
      }
    },
    [gatheringId],
  );

  const loadNotes = async (gatheringId: string) => {
    try {
      setIsLoading(true);
      const responseNotes = await gatheringsAPI.getNotes(gatheringId);

      const sortNotes = responseNotes.sort((a, b) => {
        const prevDate = +new Date(a.creationDate);
        const nextDate = +new Date(b.creationDate);
        return nextDate - prevDate;
      });

      setNotes(sortNotes);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotes(gatheringId);
  }, [gatheringId]);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <NewNote
        creating={isCreating}
        disabled={activeNoteForm}
        onUploadFile={handleUploadFile}
        onCreate={handleCreateNote}
        onCreateModeChange={setActiveNoteForm}
      />
      <div>
        {notes.map((note, index) => (
          <Note<GatheringNoteType>
            key={note.id}
            note={note}
            updating={updatingNotesIds[note.id]}
            deleting={deletingNotesIds[note.id]}
            disabledEdit={activeNoteForm}
            onEdit={handleNoteUpdate}
            onUploadFile={handleUploadFile}
            onRemove={handleNoteRemove}
            onEditModeChange={setActiveNoteForm}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

export default GatheringNotesPage;
